import { Injectable } from '@angular/core';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import * as libphonenumber from 'google-libphonenumber';
import { regions } from '../../environment/region/regions';

export interface IFormattedPhoneNumber {
    international: string;
    dashed: string;
    nationalFormat: string;
}

@Injectable()
export class PhoneProvider {
    phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
    constructor(
    ) {

    }

    /**
* Validates and updates the phone number and country code to keep them
* in sync allowing the "+" symbol in the phone input field.
*/
    public validatePhoneNumberWithPlusSign(didCountryChange: boolean, prefix, phoneParam) {
        let finalPhoneNumber = '';
        let finalSelectedCountry = '';
        try {
            let { phone, selectedCountry, numberToParse, setPhone, setCountry, setCountryPhone, select } = phoneParam;
            numberToParse = numberToParse.trim();
            if (numberToParse.substr(0, 2) === '00') {
                numberToParse = "+" + numberToParse.substr(2, numberToParse.length);
            }
            if(/^0[^0]/.test(numberToParse)) {
                numberToParse = numberToParse.slice(1);    
            }
            const parsedNumber = this.parseNumber(numberToParse, selectedCountry);

            finalPhoneNumber = numberToParse;
            if (numberToParse.startsWith("+")) {
                const newCountryCode = `+${parsedNumber.getCountryCode()}`;
                const numberWithoutCountryCode = numberToParse.substring(newCountryCode.length);
                const newParsedNumber = this.phoneUtil.parseAndKeepRawInput(numberWithoutCountryCode, this.resolveSelectedCountry(selectedCountry));
                const prevCountryCode = `+${newParsedNumber.getCountryCode()}`;
                if (didCountryChange) {     
                    if (prevCountryCode !== newCountryCode) {
                        const autoCorrectInput = numberToParse.replace(newCountryCode, "");
                        finalPhoneNumber = this.setPhoneAndRemoveSepcialCharacters(autoCorrectInput, setPhone);
                        const country = this.getCountryCode(parsedNumber, selectedCountry)
                        selectedCountry = country;
                    }
                } else {
                    let autoCorrectInput = '';
                    const country = this.getCountryCode(parsedNumber, selectedCountry)

                    if (selectedCountry !== country) {

                        if (prevCountryCode !== newCountryCode) {
                            autoCorrectInput = numberToParse.replace(newCountryCode, "");
                        }
                        selectedCountry = country === "GB" ? "UK" : country;
                        if (setCountryPhone)
                            setCountryPhone(selectedCountry);
                        window.setTimeout(() => {
                            const isSelectedValue = select.getValue();
                            if (autoCorrectInput && isSelectedValue) {
                                finalPhoneNumber = this.setPhoneAndRemoveSepcialCharacters(autoCorrectInput, setPhone);
                            }
                        }, 150);
                    } else {
                        const autoCorrectInput = numberToParse.replace(prevCountryCode, "");
                        finalPhoneNumber = this.setPhoneAndRemoveSepcialCharacters(autoCorrectInput, setPhone);
                    }
                }
            } else {
                finalPhoneNumber = this.setPhoneAndRemoveSepcialCharacters(numberToParse, setPhone);
            }
            finalSelectedCountry = selectedCountry;

        } catch (ex) {
            throw new Error(ex);
        }
        return { finalPhoneNumber, finalSelectedCountry };
    }

    public setPhoneAndRemoveSepcialCharacters(phoneNumber, setPhone) {
        const numberToParse = this.removeNumberSpecialChars(phoneNumber)
        setPhone(numberToParse);
        return numberToParse;
    }

    removeNumberSpecialChars(phoneWithSpecialChars) {
        let phoneWithoutSpaces = phoneWithSpecialChars.replace(/[^0-9]*/g, '');
        return phoneWithoutSpaces;
    }

    public validateAndParsePhoneNumber(isCountryChanged, prefix, phoneParam) {
        let { phone, selectedCountry, numberToParse, setPhone, setSelectedCountryFromParsedNumber, setCountry, select } = phoneParam;

        const allowedNumberTypes = [1, 2];
        let isValid = false;

        if (numberToParse.length < 1) {
            return;
        }
        const oldCountryCode = selectedCountry;

        const { finalPhoneNumber, finalSelectedCountry } = this.validatePhoneNumberWithPlusSign(isCountryChanged, prefix, phoneParam);
        const selcetedCountryResolved = this.resolveSelectedCountry(finalSelectedCountry);
        phone = finalPhoneNumber;
        selectedCountry = finalSelectedCountry;
        const parsedNumber = this.phoneUtil.parseAndKeepRawInput(finalPhoneNumber, selcetedCountryResolved);
        const numberType = this.phoneUtil.getNumberType(parsedNumber);
        const selectedCountryCode = regions[selectedCountry] ? regions[selectedCountry].defaultCountryCodeFrom : 1;
        const oldSelectedCountryCode = regions[oldCountryCode] ? regions[oldCountryCode].defaultCountryCodeFrom : 1;

        if (selectedCountry !== oldCountryCode) {
            setSelectedCountryFromParsedNumber(parsedNumber, selectedCountry);
            isCountryChanged = true;
        }
        if (this.phoneUtil.isValidNumber(parsedNumber) && allowedNumberTypes.includes(numberType)) {
            const isValidNumberWithOne = numberToParse.startsWith("1") && selectedCountryCode === 1;
            if (selectedCountryCode === 1)
                isValid = this.phoneUtil.isValidNumber(parsedNumber) && allowedNumberTypes.includes(numberType) && !isValidNumberWithOne;
            else
                isValid = this.phoneUtil.isValidNumber(parsedNumber) && allowedNumberTypes.includes(numberType);
        }

        if (setCountry) {
            setCountry({
                isValid: isValid,
                phone: phone,
                country: selectedCountry,
                prefix: prefix,
                isCountryChanged: isCountryChanged
            })
        }
        return { isValid, isCountryChangedPhone: isCountryChanged, selectedCountry };
    }

    public resolveNumberToParse(phone: string, input: any): string {
        // this.phone can be undefined at initialization, default to the value in the input itself or to an empty string
        return phone ? phone :
            (input && input.formControl && input.formControl.value ? input.formControl.value : "");
    }

    private parseNumber(number: string, selectedCountry: string): any {
        return number.startsWith("+") ?
            this.phoneUtil.parseAndKeepRawInput(number, null) :
            this.phoneUtil.parseAndKeepRawInput(number, this.resolveSelectedCountry(selectedCountry));
    }


    public resolveSelectedCountry(selectedCountry: string): string {
        // Handle edge case for +44. Ria=UK, Googlelib=GB.
        return selectedCountry === "UK" ? "GB" : selectedCountry;
    }

    public getCountryCode(parsedNumber, selectedCountry) {
        const selected = {
            country: selectedCountry,
            code: this.phoneUtil.getCountryCodeForRegion(selectedCountry),
        }
        const parsed = {
            country: this.phoneUtil.getRegionCodeForCountryCode(parsedNumber.getCountryCode()),
            code: parsedNumber.getCountryCode(),
        }

        const country = selected.country === parsed.country || selected.code === parsed.code ?
            selected.country : parsed.country;
        return country;
    }

    public stripDuplicateCountryCode(countryCode: string, number: string): string {
        if (!countryCode || !number) {
            return String(number || '');
        }

        const cleanCode = this.removeNumberSpecialChars(String(countryCode));
        const cleanNumber = this.removeNumberSpecialChars(String(number).replace(/^(\+|00)/, ''));

        if (!cleanCode || !cleanNumber) {
            return cleanNumber;
        }

        if (cleanNumber.startsWith(cleanCode)) {
            try {
                const fullNumber = '+' + cleanNumber;
                const parsed = this.phoneUtil.parse(fullNumber, null);
                if (this.phoneUtil.isValidNumber(parsed)) {
                    LoggerProvider.debug(this, 'stripDuplicateCountryCode: stripped duplicate cc=' + cleanCode + ', len=' + cleanNumber.length);
                    return parsed.getNationalNumber().toString();
                }
            } catch (e) {
                LoggerProvider.warning(this, LoggerEvent.InvalidPhoneNumber,
                    'stripDuplicateCountryCode: parse failed for cc=' + cleanCode + ', len=' + cleanNumber.length);
            }
        }

        return cleanNumber;
    }

    public formatPhoneNumber(countryCode: string, number: string): IFormattedPhoneNumber {
        const cleanCode = this.removeNumberSpecialChars(String(countryCode || ''));
        const nationalNumber = this.stripDuplicateCountryCode(countryCode, number);

        if (!cleanCode || !nationalNumber) {
            LoggerProvider.warning(this, LoggerEvent.InvalidPhoneNumber, 'formatPhoneNumber: missing countryCode or number, returning empty result');
            return { international: '', dashed: '', nationalFormat: '' };
        }

        // Backstop: if the validity check inside stripDuplicateCountryCode could not confirm
        // the duplicate, slice it here to avoid emitting a doubled prefix in international/dashed.
        let national = nationalNumber;
        if (cleanCode && nationalNumber.startsWith(cleanCode)) {
            LoggerProvider.warning(this, LoggerEvent.InvalidPhoneNumber,
                'formatPhoneNumber: residual duplicate cc=' + cleanCode + ' detected, slicing fallback');
            national = nationalNumber.slice(cleanCode.length);
        }

        return {
            international: '+' + cleanCode + national,
            dashed: cleanCode + '-' + national,
            nationalFormat: national
        };
    }
}


// WEBPACK FOOTER //
// ./src/providers/phone/phone.ts